import { StoreModel } from './index'
import { thunk, Thunk, Action, action } from 'easy-peasy'
import { v4 as uuidv4 } from 'uuid'
import { sendTagularEvent } from '@mortgage-pos/shared-frontend'
import { generateUserId, redactAll } from '@mortgage-pos/utils'
import {
  baseExternalIdData,
  eventTypeMap,
  formContext,
} from '@mortgage-pos/data'
import {
  EventName,
  ExternalIdType,
  FormBrand,
  FormName,
  IdentifyEvent,
  ProductViewedEvent,
  ProductClickedEvent,
  QuestionSetSlug,
  Status,
} from '@mortgage-pos/types'
import { environment } from '@mortgage-pos/ui/env'
import http from '../services/http'
import incense from '@mortgage-pos/ui/incense'

declare global {
  interface Window {
    dataLayer: Record<string, any>[]
  }
}

function isGtmEnabled(): boolean {
  const isGtmDebug = new URLSearchParams(window.location.search).has(
    'gtm_debug'
  )
  return environment.production || isGtmDebug
}

const SIMPLE_V2_FORM_VERSION = '1'

// The Cohesion snippet only queues calls until its CDN script loads, so an early
// read returns nothing and the ids are lost for the rest of the session.
async function waitForAliasSet() {
  const aliasSet = (window as any).tagular?.('getAliasSet')
  if (aliasSet) return aliasSet

  if (window.cohesionScriptLoadFailed) return null

  return new Promise((resolve) => {
    window.cohesion('tagular:ready', () =>
      resolve((window as any).tagular?.('getAliasSet'))
    )
  })
}

const FORM_NAME_BY_QUESTION_SET: Partial<Record<QuestionSetSlug, FormName>> = {
  [QuestionSetSlug.SimpleV2Purchase]: FormName.SimpleV2Application,
  [QuestionSetSlug.SimpleV2Refinance]: FormName.SimpleV2Application,
  [QuestionSetSlug.SimpleV2PurchaseFannie]: FormName.SimpleV2Application,
  [QuestionSetSlug.SimpleV2RefinanceFannie]: FormName.SimpleV2Application,
  [QuestionSetSlug.SimpleV2PurchaseRvgtDemo]:
    FormName.SimpleV2RvgtDemoApplication,
}

export const getFormNameForQuestionSet = (slug?: string) => {
  return (
    FORM_NAME_BY_QUESTION_SET[slug as QuestionSetSlug] ??
    FormName.AlfieApplication
  )
}

const isSimpleV2FormName = (formName: FormName) =>
  formName === FormName.SimpleV2Application ||
  formName === FormName.SimpleV2RvgtDemoApplication

const getFormContextForState = (
  questionnaire: StoreModel['questionnaire'],
  flowId: string,
  formId: string
) => {
  const formName = getFormNameForQuestionSet(questionnaire?.slug)
  const isSimpleV2Form = isSimpleV2FormName(formName)

  return {
    ...formContext,
    formVersion: isSimpleV2Form ? SIMPLE_V2_FORM_VERSION : flowId,
    formId,
    formName,
    formBrand: FormBrand.Sage,
  }
}

const getStepContextForState = (
  questionnaire: StoreModel['questionnaire'],
  formId: string
) => {
  const isSimpleV2Form = isSimpleV2FormName(
    getFormNameForQuestionSet(questionnaire?.slug)
  )

  if (isSimpleV2Form) {
    const pageIndex =
      typeof questionnaire?.pageIndex === 'number' ? questionnaire.pageIndex : 0
    const pageName =
      questionnaire?.active?.name ??
      questionnaire?.pages?.[pageIndex]?.name ??
      'Unknown'

    return {
      stepId: pageName,
      stepName: pageName,
      stepNumber: pageIndex,
    }
  }

  return {
    stepId: formId,
    stepName: questionnaire?.active?.name ?? 'Unknown',
    stepNumber: questionnaire?.pageIndex || 0,
  }
}

const buildTrackingContexts = (
  storeState: StoreModel,
  trackingState: TrackingModel
) => {
  const { formId, flowId } = trackingState
  const questionnaire = storeState.questionnaire

  return {
    correlationId: formId,
    formContext: getFormContextForState(questionnaire, flowId, formId),
    stepContext: getStepContextForState(questionnaire, formId),
  }
}

const getFormContext = () => ({
  ...formContext,
  formName: FormName.AlfieApplication,
  formBrand: FormBrand.Sage,
})

interface FieldInputtedPayload {
  questionName: string
  subTitle: string
  value: any
}

interface FormErroredPayload {
  errorDetails?: string
  errorMessage: string
  errorType: string
}

export interface ElementTrackingPayload {
  actionOutcome?: string
  elementType: string
  location?: string
  text?: string
  position?: string
  htmlId?: string
}

interface ElementLoadedPayload {
  elementName: string
  location?: string
  position?: string
  htmlId?: string
}

export interface TrackingModel {
  formId: string
  flowId: string
  setFlowID: Action<TrackingModel, string>
  identify: Thunk<TrackingModel, void, object, StoreModel>
  formStarted: Thunk<TrackingModel, void, object, StoreModel>
  formContinued: Thunk<TrackingModel, void, object, StoreModel>
  formSubmitted: Thunk<TrackingModel, void, object, StoreModel>
  formErrored: Thunk<TrackingModel, FormErroredPayload, object, StoreModel>
  fieldInputted: Thunk<TrackingModel, FieldInputtedPayload, object, StoreModel>
  elementClicked: Thunk<
    TrackingModel,
    ElementTrackingPayload,
    object,
    StoreModel
  >
  elementViewed: Thunk<
    TrackingModel,
    ElementTrackingPayload,
    object,
    StoreModel
  >
  elementLoaded: Thunk<TrackingModel, ElementLoadedPayload, object, StoreModel>
  productViewed: Thunk<TrackingModel, ProductViewedEvent, object, StoreModel>
  productClicked: Thunk<TrackingModel, ProductClickedEvent, object, StoreModel>
  uploadDocumentManually: Thunk<TrackingModel, void, object, StoreModel>
  plaidAccountConnected: Thunk<TrackingModel, void, object, StoreModel>
  fannieAccountConnected: Thunk<TrackingModel, void, object, StoreModel>
  trackSplitFlowCompletedInGtm: Thunk<TrackingModel, void, object, StoreModel>
  trackCreditPullInGtm: Thunk<
    TrackingModel,
    { success: boolean },
    object,
    StoreModel
  >
  trackHomeEquityOffersShownInGtm: Thunk<
    TrackingModel,
    { loanAmount: number },
    object,
    StoreModel
  >
  updateTrackingData: Thunk<TrackingModel, void, object, StoreModel>
}

const tracking = (): TrackingModel => ({
  formId: uuidv4(),
  flowId: '0',
  setFlowID: action((state, payload) => {
    state.flowId = payload
  }),
  identify: thunk((_, __, { getStoreState }) => {
    const { bankrateLeadId } = getStoreState().questionnaire
    const { bankrateAnswers } = getStoreState().answers

    const event: IdentifyEvent = {
      userId: generateUserId(bankrateAnswers.email),
      traits: {
        address: {
          state: bankrateAnswers.state,
        },
        email: bankrateAnswers.email,
        firstName: bankrateAnswers.firstName,
        lastName: bankrateAnswers.lastName,
        phone: bankrateAnswers.phone,
      },
      externalIds: [
        {
          id: bankrateLeadId,
          type: ExternalIdType.Bankrate,
          ...baseExternalIdData,
        },
      ],
    }

    sendTagularEvent(EventName.Identify, event)
  }),

  formStarted: thunk((_, __, { getStoreState, getState }) => {
    const event = buildTrackingContexts(getStoreState(), getState())

    sendTagularEvent(EventName.FormStarted, event)
  }),

  formContinued: thunk((_, __, { getStoreState, getState }) => {
    const dataLayer = window?.dataLayer || []
    const { active } = getStoreState().questionnaire

    // Send the event to dataLayer for GTM to capture so we can reference the step name in the GTM tag
    if (isGtmEnabled() && dataLayer) {
      dataLayer.push({
        event: eventTypeMap[EventName.FormContinued],
        fieldName: 'stepName',
        fieldValue: active?.name ?? 'Unknown',
      })
    }

    const event = buildTrackingContexts(getStoreState(), getState())

    sendTagularEvent(EventName.FormContinued, event)
  }),

  fieldInputted: thunk((_, payload, { getStoreState, getState }) => {
    const dataLayer = window?.dataLayer || []
    const { active, pageIndex } = getStoreState().questionnaire
    const { questionName, subTitle, value } = payload
    const { formId, flowId } = getState()

    const redactedFields = {
      ...redactAll({ [questionName]: value }),
    }
    const event = {
      correlationId: formId,
      formContext: {
        ...getFormContext(),
        formVersion: flowId,
        formId,
      },
      stepContext: {
        stepId: formId,
        stepName: active?.name,
        stepNumber: pageIndex,
      },
      userInputField: {
        fieldType: active?.section ?? 'Unknown',
        fieldName: questionName,
        fieldLabel: subTitle,
        fieldValue: String(redactedFields[questionName]),
      },
    }

    if (isGtmEnabled() && dataLayer) {
      dataLayer.push({
        event: eventTypeMap[EventName.FieldInputted],
        fieldName: questionName,
        fieldValue: String(redactedFields[questionName]),
      })
    }
    sendTagularEvent(EventName.FieldInputted, event)
  }),

  formErrored: thunk(
    (
      _,
      { errorDetails = '', errorMessage, errorType },
      { getStoreState, getState }
    ) => {
      const event = {
        ...buildTrackingContexts(getStoreState(), getState()),
        errorDetails, //additional details
        errorMessage, // <error message shown to the user>,
        errorType, //<short description of error - aka "format error">,
      }

      sendTagularEvent(EventName.FormErrored, event)
    }
  ),

  formSubmitted: thunk((_, __, { getStoreState, getState }) => {
    const { pageIndex } = getStoreState().questionnaire
    const { formId, flowId } = getState()
    const event = {
      correlationId: formId,
      formContext: {
        ...getFormContext(),
        formVersion: flowId,
        formId,
      },
      stepContext: {
        stepId: formId,
        stepName: 'rateSelection',
        stepNumber: pageIndex + 1, // increment one from last page in questionnaire
      },
    }

    sendTagularEvent(EventName.FormSubmitted, event)
  }),

  elementClicked: thunk((_, payload, { getStoreState, getState }) => {
    const {
      actionOutcome = '',
      elementType = '',
      location = '',
      text = '',
    } = payload

    const { formId } = getState()

    const event = {
      correlationId: formId,
      webElement: {
        elementType,
        location,
        text,
      },
      actionOutcome,
    }

    sendTagularEvent(EventName.ElementClicked, event)
  }),

  elementViewed: thunk((_, payload, { getStoreState, getState }) => {
    const {
      position = '',
      elementType = '',
      location = '',
      text = '',
      htmlId = '',
    } = payload

    const event = {
      webElement: {
        elementType,
        location,
        text,
        position,
        htmlId,
      },
    }

    sendTagularEvent(EventName.ElementViewed, event)
  }),

  elementLoaded: thunk((_, payload) => {
    const { elementName, location = '', position = '', htmlId = '' } = payload

    sendTagularEvent(EventName.ElementLoaded, {
      webElement: {
        elementType: 'lifecycle_milestone',
        location,
        ...(position ? { position } : {}),
        ...(htmlId ? { htmlId } : {}),
        text: elementName,
      },
    })
  }),

  productViewed: thunk((_, productViewedEvent, { getState }) => {
    sendTagularEvent(EventName.ProductViewed, productViewedEvent)
  }),

  productClicked: thunk((_, productClickedEvent, { getState }) => {
    sendTagularEvent(EventName.ProductClicked, productClickedEvent)
  }),

  uploadDocumentManually: thunk((_, __, { getState }) => {
    const { formId, flowId } = getState()
    const event = {
      correlationId: formId,
      formContext: {
        ...getFormContext(),
        formVersion: flowId,
        formId,
      },
      stepContext: {
        stepId: formId,
        stepNumber: 0,
      },
    }
    sendTagularEvent(EventName.UploadDocumentManually, event)
  }),

  plaidAccountConnected: thunk((_, __, { getState }) => {
    const { formId, flowId } = getState()
    const event = {
      correlationId: formId,
      formContext: {
        ...getFormContext(),
        formVersion: flowId,
        formId,
      },
      stepContext: {
        stepId: formId,
        stepNumber: 0,
      },
    }
    sendTagularEvent(EventName.PlaidAccountConnected, event)
  }),

  fannieAccountConnected: thunk((_, __, { getState }) => {
    const { formId, flowId } = getState()
    const event = {
      correlationId: formId,
      formContext: {
        ...getFormContext(),
        formVersion: flowId,
        formId,
      },
      stepContext: {
        stepId: formId,
        stepNumber: 0,
      },
    }
    sendTagularEvent(EventName.FannieAccountConnected, event)
  }),

  // Pushes a GTM event when a split flow (pre-pricing or apply) completes
  trackSplitFlowCompletedInGtm: thunk((_, __, { getState, getStoreState }) => {
    const dataLayer = window?.dataLayer || []
    const { formId } = getState()
    const { isPrePricingFlow } = getStoreState().questionnaire

    if (!isGtmEnabled() || !dataLayer) return

    dataLayer.push({
      event: isPrePricingFlow
        ? Status.PrePricingFlowCompleted
        : Status.ApplyFlowCompleted,
      formId,
    })
  }),

  // Pushes a GTM event after a soft credit pull resolves, with success/failure outcome.
  trackCreditPullInGtm: thunk((_, { success }, { getState }) => {
    const dataLayer = window?.dataLayer || []
    const { formId } = getState()

    if (!isGtmEnabled() || !dataLayer) return

    dataLayer.push({
      event: 'creditPullCompleted',
      creditPullSuccess: success,
      formId,
    })
  }),

  // Pushes a GTM event when home equity offers are shown to the user.
  trackHomeEquityOffersShownInGtm: thunk((_, { loanAmount }, { getState }) => {
    const dataLayer = window?.dataLayer || []
    const { formId } = getState()

    if (!isGtmEnabled() || !dataLayer) return

    dataLayer.push({
      event: 'offersShown',
      formId,
      loanAmount,
    })
  }),

  // Update the application_tracking table with the tracking data
  // only when Cohesion exposes a truthy alias set from tagular('getAliasSet').
  updateTrackingData: thunk(async (_, __, { getStoreState }) => {
    let applicationGuid

    try {
      const aliasSet = await waitForAliasSet()

      if (aliasSet) {
        applicationGuid = getStoreState().application.applicationGuid

        const trackingData = {
          anonymousId: aliasSet.anonymousId,
          instanceId: aliasSet.instanceId,
          sessionId: aliasSet.sessionId,
          transitId: aliasSet.transitId,
          tenantId: aliasSet.tenantId,
        }

        await http.post(
          `${environment.apiBaseUrl}/tracking/update-tracking-data`,
          {
            applicationGuid,
            trackingData,
          }
        )
      }
    } catch (error) {
      incense(error)
        .details({
          name: 'Error updating tracking data',
          message: error.message,
        })
        .safe({ applicationGuid })
        .sensitive({ error })
        .error()
    }
  }),
})

export default tracking
